/**
 * Chat Assistant — client state store.
 *
 * Integration guide §4 (Client-to-Server Actions), §5 (Server-to-Client Events),
 * §7 (Streaming a Reply), §8 (Stopping a Reply), §9 (Multi-Tab / Multi-Device
 * Behavior), §10 (Error Handling), §11 (Recommended Client State Model),
 * §12 (Reconnect Strategy).
 *
 * Chat history: the server stores every conversation (§4), and this client
 * reads it back. `channels` is the user's conversation list, refreshed on every
 * (re)connect; opening one sends `loadHistory` and renders the `history` frame.
 * Rename and delete go through the server and are applied from its broadcasts
 * (§13 step 7), so every open tab converges on the same list.
 *
 * Only the open conversation's messages are held, as a flat array, rather than
 * the `messagesByChannel` map §11 suggests. Switching conversations always
 * refetches — the server is the source of truth, and the conversation may have
 * moved on from another tab — so a cache of the others would only ever be shown
 * stale. Nothing is persisted in the browser.
 */

import { create } from "zustand";
import { queryClient } from "core/query/queryClient";
import { Hub } from "aws-amplify/utils";
import { ChatAssistantSocket } from "./socket";
import type { ChannelListEvent, ChannelSummary, ChatMessage, ChunkEvent, DoneEvent, HistoryEvent, HistoryMessage, LeadUploadEvent, ProgressEvent, ReplyProgressEvent, ResponseEvent, ServerEvent, ThinkingEvent, SocketError, SocketStatus, StoppedEvent, TemplateGeneratedEvent, TemplateSaveRequestEvent } from "./types";
import { runLeadUpload, type LeadUploadResult } from "./leadUpload";
import { getTemplateEditorBridge, runTemplateSave } from "./templateBridge";
import { isImportFinished, mergeImportProgress } from "./importProgress";
import { publishJobProgress } from "core/features/integrations/jobProgress";
import { customersKeys, customerGroupKeys } from "core/features/leads/hooks";

/**
 * Handoff CA-6: `template_generated.html` now carries **raw HTML**, and a
 * markdown fence must not be required.
 *
 * This strips a fence only when one actually matches, so raw HTML passes
 * through untouched and a fenced document — which older deployments still send,
 * and which the model can still produce — is unwrapped rather than rendered as
 * text. Every consumer treats the field as a document (the preview feeds it to
 * `srcDoc`, the save path stores it), so it happens once here at the wire
 * boundary rather than in each.
 *
 * The two markers are removed independently, on purpose. A single pattern
 * spanning both is all-or-nothing: a reply that opened a fence and never closed
 * it, or one whose document happened to contain another ``` further down, failed
 * to match as a whole and the opening "```html" was handed to the iframe as
 * content — where it rendered as the first line of the email.
 */
export function normalizeTemplateHtml(raw: string): string {
    return raw
        .trim()
        .replace(/^```[a-zA-Z0-9+-]*[ \t]*\r?\n?/, "")
        .replace(/[ \t]*\r?\n?```[ \t]*$/, "")
        .trim();
}

// ─── module-scoped, non-reactive internals ─────────────────────────────────

let socket: ChatAssistantSocket | null = null;

/** True when *this* tab issued `createChannel` (the event itself is broadcast). */
let awaitingChannelCreate = false;
/** Prompt held back until `channel_created` arrives for a brand-new conversation. */
let pendingPrompt: string | null = null;
/** Last prompt sent, for the §10 retry affordance. */
let lastAttempt: { channelId: string; prompt: string } | null = null;
/** The newest upload note's bar, until an import's frames claim it. */
let csvImportMessageId: string | null = null;
/**
 * The audience the last `lead_upload` was for, so a failed import can be
 * retried (CA-3).
 *
 * CA-3 says to retry "by requesting a new presigned upload". The client cannot
 * mint one — the target is single-use and expires, and §4 has no action that
 * asks for one — so the retry goes back through the only channel there is: a
 * prompt to the assistant, which is what produced the `lead_upload` frame in
 * the first place.
 */
let lastImportGroupId: number | null = null;
/** Imports that completed; late or repeated frames for them are dropped. */
const finishedImportJobs = new Set<string>();
/** Per-bar timers that mark an import stalled when its frames stop. */
const importStallTimers = new Map<string, ReturnType<typeof setTimeout>>();
/** Channels already re-listed once to pick up the server's assigned title. */
const titleRefetched = new Set<string>();
/** No CSV frame for this long marks a bar stalled. A later frame revives it. */
const IMPORT_STALL_MS = 120_000;
/**
 * A bar that has never had a single frame gives up sooner.
 *
 * Measured against prod: a CSV that uploads cleanly (S3 204) can draw no
 * `progress` frame at all — the import simply never reports. Two minutes of
 * "Importing contacts… 0%" before saying so is two minutes of a bar that was
 * never moving, so silence from the start is called much earlier than an
 * import that stopped partway.
 */
const IMPORT_NEVER_STARTED_MS = 25_000;

const clearImportTracking = () => {
    csvImportMessageId = null;
    lastImportGroupId = null;
    finishedImportJobs.clear();
    importStallTimers.forEach(clearTimeout);
    importStallTimers.clear();
};
/**
 * `listChannels` requests awaiting a `channel_list` frame. Coalesces duplicate
 * refreshes, and lets an untyped API Gateway failure be attributed to the list
 * rather than reported as though the user's message had failed.
 */
let pendingListChannels = 0;
/** The channel whose `loadHistory` is in flight; a `history` frame for any other is stale. */
let historyRequestedFor: string | null = null;
/** Guards against attaching more than one Hub subscription. */
let signOutWatcherAttached = false;

let localIdCounter = 0;
const nextLocalId = (prefix: string) => `local-${prefix}-${Date.now()}-${++localIdCounter}`;

const EMPTY_MESSAGES: ChatMessage[] = [];
const EMPTY_CHANNELS: ChannelSummary[] = [];

/** Local placeholder until a new channel is named. */
export const NEW_CHANNEL_TITLE = "New chat";

/*
 * Handoff CA-4: the server names a conversation itself — "the first user
 * message may trigger an automatic title update" — and announces it with
 * `channel_renamed`.
 *
 * This client used to derive a title from the opening prompt and send
 * `renameChannel` for it, on the observation that the server named nothing
 * (208 of 209 prod channels were still "New chat"). That premise no longer
 * holds, and two renames racing on the same trigger make the winner arbitrary,
 * so the client no longer titles anything on its own. Titles now arrive only
 * from the broadcast, and a conversation that never gets one simply keeps the
 * placeholder — CA-4 is explicit that a missing event is not a failed turn.
 *
 * Renaming by hand from the sidebar is unaffected: that is a deliberate user
 * action, and `renameChannel` remains the documented way to do it.
 */

/** A stored message, in the shape the live conversation uses. */
const fromHistory = (message: HistoryMessage): ChatMessage => ({
    clientId: message.message_id,
    message_id: message.message_id,
    sender: message.sender,
    message: message.message,
    created_at_ms: message.created_at_ms,
    status: "done",
    ...(message.thinking?.length && {
        thinking: message.thinking.map((text, sequence) => ({ sequence, text })),
    }),
});

/**
 * Stored history, plus whatever on screen the server does not have yet.
 *
 * A reload of the open conversation (reconnect, or another tab finishing a
 * reply in it) must not wipe what this tab is showing:
 *
 *   • a reply still streaming here keeps its live bubble, even where the server
 *     already stored a partial copy under the same id;
 *   • upload notes and import bars exist only in this tab, so they are kept;
 *   • an optimistic user turn is dropped once the server has stored it, so it
 *     is not shown twice.
 */
export function mergeHistory(stored: ChatMessage[], current: ChatMessage[]): ChatMessage[] {
    const isLive = (m: ChatMessage) => m.status === "pending" || m.status === "streaming";
    const live = new Map(current.filter(isLive).map((m) => [m.message_id, m]));
    const storedIds = new Set(stored.map((m) => m.message_id));
    const storedPrompts = new Set(stored.filter((m) => m.sender === "user").map((m) => m.message));

    const kept = current.filter((m) => {
        if (storedIds.has(m.message_id)) return false;
        if (isLive(m)) return true;
        if (!m.isLocalId) return false;
        return m.sender === "assistant" || !storedPrompts.has(m.message);
    });

    // A stored message already on screen keeps the identity it was drawn with,
    // so a reload does not re-mount and re-animate what the user is reading.
    const drawn = new Map(current.map((m) => [m.message_id, m.clientId]));
    const settle = (m: ChatMessage) => {
        const clientId = drawn.get(m.message_id);
        return clientId && clientId !== m.clientId ? { ...m, clientId } : m;
    };

    return [...stored.map((m) => live.get(m.message_id) ?? settle(m)), ...kept];
}

// ─── state ─────────────────────────────────────────────────────────────────

export interface ChatAssistantState {
    // Guide §11.
    socketStatus: SocketStatus;
    activeChannelId: string | null;
    /** The user's conversations, newest first. */
    channels: ChannelSummary[];
    /** Messages of the open conversation only. */
    messages: ChatMessage[];
    activeGenerationId: string | null;
    activeAssistantMessageId: string | null;
    isStreaming: boolean;
    lastSocketError: SocketError | null;

    /** True after `stopMessage` is sent, until `stopped` arrives (§8 is best effort). */
    isStopping: boolean;

    /** True between `listChannels` and the `channel_list` frame. */
    isLoadingChannels: boolean;
    /**
     * Failure of a conversation-list refresh. Kept apart from `lastSocketError`
     * so a broken list never raises a banner that reads as "your message failed".
     */
    channelsError: string | null;
    /** True while the open conversation's stored messages are being fetched. */
    isHistoryLoading: boolean;

    /**
     * When the current generation last produced text, or the moment it started.
     *
     * The socket sends nothing at all while the assistant runs a tool: a
     * template generation is ~16s of complete silence between the last chunk of
     * "I'll generate a Diwali email template for you." and `template_generated`.
     * Only the *absence* of chunks marks that stretch, so the screen needs the
     * timestamp to tell "still working" apart from "finished talking".
     */
    lastChunkAtMs: number | null;

    /** Last template the assistant generated, held so the builder can pick it
        up even when the user was on the chat screen when it arrived. */
    generatedTemplate: {
        html: string;
        sessionId: string | null;
        receivedAtMs: number;
    } | null;

    /** In-flight or last `template_save_request`. `needs-name` means the
        assistant sent none and the screen must ask, as manual save does. */
    templateSave: {
        status: "saving" | "saved" | "needs-name" | "failed";
        name?: string | null;
        reason?: string;
        /** CA-6: the document itself was rejected (422 `invalid_model_output`). */
        invalidModelOutput?: boolean;
    } | null;

    /** Completes a save that arrived without a name, once the user supplies one. */
    submitTemplateName: (name: string) => Promise<void>;
    dismissTemplateSave: () => void;

    /** In-flight or last `lead_upload`, so the composer can show what is happening. */
    leadUpload: {
        status: "picking" | "uploaded" | "cancelled" | "rejected" | "failed";
        customerGroupId: number;
        fileName?: string;
        reason?: string;
    } | null;

    // Lifecycle.
    connect: () => void;
    disconnect: () => void;
    retryConnection: () => void;

    // Guide §4.
    sendPrompt: (prompt: string) => void;
    stopMessage: () => void;
    listChannels: () => void;
    renameChannel: (channelId: string, title: string) => void;
    deleteChannel: (channelId: string) => void;

    // UI helpers.
    /** Drops a generated template once the builder has opened it. */
    clearGeneratedTemplate: () => void;

    /** Open a stored conversation and load its history. */
    selectChannel: (channelId: string) => void;
    /** Leave the open conversation (it stays in the list) and begin a new one. */
    startNewChat: () => void;
    retryLastPrompt: () => void;
    canRetry: () => boolean;
    clearError: () => void;

    /**
     * CA-3: retry a failed contact import by asking for a new presigned upload.
     *
     * Returns false when there is nothing to retry — no import has been
     * requested in this conversation, or a reply is already streaming.
     */
    retryContactImport: () => boolean;
    canRetryContactImport: () => boolean;
}

export const useChatAssistantStore = create<ChatAssistantState>((set, get) => {
    // ── §5 event handling ──────────────────────────────────────────────────

    type GenerationFrame = ChunkEvent | ThinkingEvent | ResponseEvent | ReplyProgressEvent;

    /**
     * Guide §7: apply a generation frame to the assistant message it belongs to.
     *
     * The first frame of a generation *binds* it to the optimistic bubble drawn
     * when the prompt was sent — until then the client has no
     * `message_id`/`generation_id` to match on. That first frame is usually a
     * `thinking` line now, not a chunk.
     *
     * After that, frames are matched by `generation_id` on the message itself.
     * A frame matching no message here is another tab's or a stale one (§11).
     */
    const applyToGeneration = (
        frame: GenerationFrame,
        patch: (message: ChatMessage) => Partial<ChatMessage>,
    ) =>
        set((state) => {
            if (frame.channel_id !== state.activeChannelId) return state;

            const owned = state.messages.some((m) => m.generation_id === frame.generation_id);
            const canBind =
                !owned && !state.activeGenerationId && state.isStreaming && !!state.activeAssistantMessageId;
            if (!owned && !canBind) return state;

            const isActive = canBind || frame.generation_id === state.activeGenerationId;
            const messages = state.messages.map((message) => {
                const matches = canBind
                    ? message.message_id === state.activeAssistantMessageId
                    : message.generation_id === frame.generation_id;
                if (!matches) return message;
                return {
                    ...message,
                    ...patch(message),
                    message_id: frame.message_id,
                    generation_id: frame.generation_id,
                    status: isActive ? ("streaming" as const) : message.status,
                    isLocalId: false,
                };
            });

            if (!isActive) return { messages };
            return {
                messages,
                activeGenerationId: frame.generation_id,
                activeAssistantMessageId: frame.message_id,
                isStreaming: true,
                lastChunkAtMs: Date.now(),
            };
        });

    /** Chunks now only signal "still generating"; the text is kept as a fallback. */
    const handleChunk = (frame: ChunkEvent) =>
        applyToGeneration(frame, (message) => ({
            streamedText: (message.streamedText ?? "") + frame.text,
        }));

    const handleThinking = (frame: ThinkingEvent) =>
        applyToGeneration(frame, (message) => {
            const steps = message.thinking ?? [];
            if (steps.some((step) => step.sequence === frame.sequence)) return {};
            return {
                thinking: [...steps, { sequence: frame.sequence, text: frame.text }].sort(
                    (a, b) => a.sequence - b.sequence,
                ),
                thinkingStartedAtMs: message.thinkingStartedAtMs ?? Date.now(),
            };
        });

    /** How long a reply worked, for the line its steps collapse into. */
    const workedFor = (message: ChatMessage) =>
        message.thinkingStartedAtMs && message.thinkingMs === undefined
            ? { thinkingMs: Date.now() - message.thinkingStartedAtMs }
            : {};

    const handleResponse = (frame: ResponseEvent) =>
        applyToGeneration(frame, (message) => ({
            message: frame.text,
            hasResponse: true,
            ...workedFor(message),
        }));

    /**
     * (Re)arms the stall timer for an import bar. A bar whose frames stop —
     * the import failed server-side, or never started — is marked stalled
     * rather than left "Importing…" forever.
     */
    const watchImportStall = (messageId: string, done: boolean, everReported = true) => {
        clearTimeout(importStallTimers.get(messageId));
        importStallTimers.delete(messageId);
        if (done) return;

        importStallTimers.set(
            messageId,
            setTimeout(() => {
                importStallTimers.delete(messageId);
                set((state) => ({
                    messages: state.messages.map((m) =>
                        m.message_id === messageId && m.progress && !isImportFinished(m.progress)
                            ? { ...m, progress: { ...m.progress, status: "stalled" as const } }
                            : m,
                    ),
                }));
            }, everReported ? IMPORT_STALL_MS : IMPORT_NEVER_STARTED_MS),
        );
    };

    /**
     * Contact ingestion the assistant runs during a reply. The bar belongs to
     * the reply itself, drawn between its thinking lines and its answer, and is
     * matched like any other generation frame. It can outlive the reply — the
     * contacts are queued, then imported — so it is still matched after `done`.
     */
    const handleReplyProgress = (frame: ReplyProgressEvent) => {
        let finished = false;
        let applied = false;
        applyToGeneration(frame, (message) => {
            const progress = mergeImportProgress(message.progress, frame);
            finished = isImportFinished(progress);
            applied = true;
            return { progress };
        });
        if (!applied) return;

        watchImportStall(frame.message_id, finished);
        if (!finished) return;
        // Even a failed import may have added some contacts.
        queryClient.invalidateQueries({ queryKey: customersKeys.root });
        queryClient.invalidateQueries({ queryKey: customerGroupKeys.root });
    };

    /**
     * CSV import progress, matched to a bar by `job_id`:
     *
     *   1. the bar already following this job;
     *   2. otherwise the newest upload note's bar, if no job has claimed it;
     *   3. otherwise a message of its own (an import this tab did not start).
     *
     * Frames for a job that already completed are dropped, so a late or
     * repeated frame cannot open a second bar. Frames without `job_id` fall
     * back to the newest upload's bar.
     */
    const handleProgress = (frame: ProgressEvent) => {
        /* Integrations job progress. A different producer with different
           semantics: no channel, no generation, matched by `job_id` alone, and
           explicitly best-effort — so it is handed to the integrations
           projection and never folded into a csv or reply bar. The handoff is
           equally explicit that csv frames must not be reinterpreted, which is
           why this routes on `source` before anything else looks at the frame. */
        if (frame.source === "integration") {
            publishJobProgress(frame);
            return;
        }

        if (frame.source !== "csv") {
            if (frame.generation_id && frame.message_id && frame.channel_id) {
                handleReplyProgress(frame as ReplyProgressEvent);
            }
            return;
        }
        const jobId = frame.job_id;
        if (jobId && finishedImportJobs.has(jobId)) return;

        let barId = "";
        let completed = false;

        set((state) => {
            const open = (m: ChatMessage) => !!m.progress && !isImportFinished(m.progress);
            const target =
                (jobId && state.messages.find((m) => open(m) && m.progress!.jobId === jobId)) ||
                state.messages.find(
                    (m) => m.message_id === csvImportMessageId && open(m) && !(jobId && m.progress!.jobId),
                );

            const progress = mergeImportProgress(target ? target.progress : undefined, frame);
            completed = isImportFinished(progress);

            if (target) {
                barId = target.message_id;
                return {
                    messages: state.messages.map((m) => (m.message_id === barId ? { ...m, progress } : m)),
                };
            }

            barId = nextLocalId("import");
            if (!jobId) csvImportMessageId = barId;
            return {
                messages: [
                    ...state.messages,
                    {
                        clientId: barId,
                        message_id: barId,
                        sender: "assistant" as const,
                        message: "",
                        created_at_ms: Date.now(),
                        status: "done" as const,
                        isLocalId: true,
                        progress,
                    },
                ],
            };
        });

        watchImportStall(barId, completed);
        if (!completed) return;

        if (jobId) finishedImportJobs.add(jobId);
        // The imported contacts are not in any cache until now.
        queryClient.invalidateQueries({ queryKey: customersKeys.root });
        queryClient.invalidateQueries({ queryKey: customerGroupKeys.root });
    };

    /** Final text for a message with no `response`: whatever the chunks streamed. */
    const settledText = (message: ChatMessage) =>
        message.hasResponse ? message.message : message.message || message.streamedText || "";

    /** Guide §7 finalization, for the generation this tab is streaming. */
    const finalizeGeneration = (frame: DoneEvent | StoppedEvent) =>
        set((state) => {
            if (frame.generation_id !== state.activeGenerationId) return state;

            return {
                messages: state.messages.map((message) =>
                    message.message_id === frame.message_id
                        ? {
                            ...message,
                            message: settledText(message),
                            status: frame.type === "done" ? ("done" as const) : ("stopped" as const),
                            ...workedFor(message),
                        }
                        : message,
                ),
                activeGenerationId: null,
                activeAssistantMessageId: null,
                isStreaming: false,
                isStopping: false,
                lastChunkAtMs: null,
            };
        });

    /**
     * `done`/`stopped`. §9 broadcasts them to every connection for this user, so
     * a frame for a generation this tab never streamed is another tab's — or one
     * this tab left behind by switching away. If it finished in the conversation
     * on screen, the guide's sync rule applies: reload that history.
     */
    const handleGenerationEnd = (frame: DoneEvent | StoppedEvent) => {
        const { activeGenerationId, activeChannelId, isStreaming } = get();
        if (frame.generation_id === activeGenerationId) finalizeGeneration(frame);
        else if (frame.channel_id === activeChannelId) {
            // Our reply ended before any frame bound it — typically Stop pressed
            // during the first second or two (§8). Its text, if any, is stored.
            if (isStreaming && activeGenerationId === null) endUnboundGeneration();
            requestHistory(frame.channel_id);
        }
        touchChannel(frame.channel_id);
    };

    /** Ends local streaming and drops the empty placeholder; the reload fills the reply in. */
    const endUnboundGeneration = () =>
        set((state) => ({
            messages: state.messages.filter(
                (m) => m.message_id !== state.activeAssistantMessageId || !!settledText(m),
            ),
            activeGenerationId: null,
            activeAssistantMessageId: null,
            isStreaming: false,
            isStopping: false,
            lastChunkAtMs: null,
        }));

    /**
     * A reply finished in this channel: move it to the top of the list.
     *
     * A channel still carrying the local placeholder is re-listed **once** to
     * pick up the title the server assigns from the first user message (CA-4).
     * The broadcast is the primary path; this is the reconcile fallback for a
     * `channel_renamed` that never arrived. It stays a single check because
     * re-listing on every reply re-fetched ~31 KB per turn, forever.
     */
    const touchChannel = (channelId: string) => {
        const channel = get().channels.find((c) => c.channel_id === channelId);
        if (!channel) return;
        set((state) => ({
            channels: [
                { ...channel, last_updated: Date.now() },
                ...state.channels.filter((c) => c.channel_id !== channelId),
            ],
        }));
        const untitled = !channel.title || channel.title === NEW_CHANNEL_TITLE;
        if (untitled && !titleRefetched.has(channelId)) {
            titleRefetched.add(channelId);
            requestChannelList();
        }
    };

    // ── conversation management ────────────────────────────────────────────

    /**
     * The list replaces what is held, with one exception: the open conversation
     * is kept even when missing, since a list requested just before it was
     * created can arrive after `channel_created`. Deletion is signalled by
     * `channel_deleted`, not by absence.
     */
    const handleChannelList = (frame: ChannelListEvent) => {
        pendingListChannels = 0;
        const channels = Array.isArray(frame.channels) ? frame.channels : EMPTY_CHANNELS;
        set((state) => {
            const missing = state.channels.filter(
                (c) => c.channel_id === state.activeChannelId && !channels.some((n) => n.channel_id === c.channel_id),
            );
            return { channels: [...missing, ...channels], isLoadingChannels: false, channelsError: null };
        });
    };

    const handleHistory = (frame: HistoryEvent) => {
        if (frame.channel_id === historyRequestedFor) historyRequestedFor = null;
        // A conversation the user has since left.
        if (frame.channel_id !== get().activeChannelId) return;

        const stored = (frame.messages ?? []).map(fromHistory);
        set((state) => ({
            messages: mergeHistory(stored, state.messages),
            isHistoryLoading: false,
        }));
    };

    /** Guide §10: end the local streaming state and surface a retry option. */
    const failActiveStream = (message: string) =>
        set((state) => {
            const targetId = state.activeAssistantMessageId;
            return {
                messages: targetId
                    ? state.messages.map((entry) =>
                        entry.message_id === targetId
                            ? { ...entry, status: "error" as const, message: settledText(entry) || message, ...workedFor(entry) }
                            : entry,
                    )
                    : state.messages,
                activeGenerationId: null,
                activeAssistantMessageId: null,
                isStreaming: false,
                isStopping: false,
                lastChunkAtMs: null,
            };
        });

    /**
     * Picks a CSV and posts it to the presigned target.
     *
     * The outcome is written back into the conversation. An upload that only
     * succeeds or fails silently leaves the user staring at a file dialog that
     * closed, with no way to tell whether anything happened — and the assistant
     * asked for this file, so the assistant is where the answer belongs.
     */
    const handleLeadUpload = async (frame: LeadUploadEvent) => {
        const startedAt = Date.now();
        lastImportGroupId = frame.customer_group_id;
        set({ leadUpload: { status: "picking", customerGroupId: frame.customer_group_id } });

        const result: LeadUploadResult = await runLeadUpload(frame);

        set({
            leadUpload: {
                status: result.status,
                customerGroupId: result.customerGroupId,
                fileName: result.fileName,
                reason: result.reason,
            },
        });

        if (result.status === "cancelled") return;

        const note =
            result.status === "uploaded"
                ? `Uploaded **${result.fileName}**. The contacts will appear in this audience once processing finishes.`
                : `Could not upload${result.fileName ? ` **${result.fileName}**` : ""}. ${result.reason ?? ""}`.trim();

        const id = nextLocalId("assistant");
        let showBar = false;

        set((state) => {
            // The note carries this import's bar from 0% — unless the import's
            // first frames beat the note here and already opened one.
            const alreadyTracked = state.messages.some(
                (m) => m.progress && !m.message && m.created_at_ms >= startedAt,
            );
            showBar = result.status === "uploaded" && !alreadyTracked;
            if (showBar) csvImportMessageId = id;

            return {
                messages: [
                    ...state.messages,
                    {
                        clientId: id,
                        message_id: id,
                        sender: "assistant" as const,
                        message: note,
                        created_at_ms: Date.now(),
                        status: "done" as const,
                        isLocalId: true,
                        ...(showBar && { progress: { percent: 0, status: "started" as const } }),
                    },
                ],
            };
        });

        /* Nothing is invalidated here: the contacts are imported after the
           upload, and the import's final progress frame refreshes the lists. */
        if (showBar) watchImportStall(id, false, false);
    };

    /**
     * §5 — the assistant generated a template.
     *
     * The HTML is held here whether or not an editor is mounted: the user asks
     * for this from /ai-playground, so usually one is not. A mounted editor is
     * updated at once; otherwise the builder picks it up when it next opens.
     *
     * Nothing is written into the conversation. The assistant announces the
     * template itself, in the chunks that follow this frame — "Your Diwali
     * template is ready in the editor…" — so a line composed here appeared
     * underneath as a second, blunter copy of what it had just said. Every
     * assistant turn on screen is now text the assistant actually sent.
     */
    const handleTemplateGenerated = (frame: TemplateGeneratedEvent) => {
        const html = normalizeTemplateHtml(frame.html);
        set({
            generatedTemplate: {
                html,
                sessionId: frame.session_id ?? null,
                receivedAtMs: Date.now(),
            },
        });

        // The builder is open: replace what is on screen, in place.
        getTemplateEditorBridge()?.loadHtml(html);
    };

    /**
     * §5 — save what was generated, through the same path the Save button uses.
     *
     * A null name is not an error: the assistant simply did not pick one, and
     * the normal save UI asks in that case. `templateSave.status` carries that
     * back to the screen so it can prompt.
     */
    const handleTemplateSaveRequest = async (frame: TemplateSaveRequestEvent) => {
        const html = get().generatedTemplate?.html ?? null;
        set({ templateSave: { status: "saving", name: frame.name } });

        const outcome = await runTemplateSave({ name: frame.name, fallbackHtml: html });
        set({
            templateSave: {
                status: outcome.status,
                name: outcome.name ?? frame.name,
                reason: outcome.reason,
                invalidModelOutput: outcome.invalidModelOutput,
            },
        });

        /* No line is composed here. The outcome belongs on the card that asked
           for the name, not in the conversation as a turn the assistant never
           sent — and next to the assistant's own "your template has been
           saved" it read as a second, contradictory opinion. */
        if (outcome.status === "saved") {
            queryClient.invalidateQueries({ queryKey: ["templates"] });
            queryClient.invalidateQueries({ queryKey: ["userTemplates"] });
        }
    };

    const handleEvent = (frame: ServerEvent) => {
        switch (frame.type) {
            case "chunk":
                handleChunk(frame);
                break;

            case "thinking":
                handleThinking(frame);
                break;

            case "response":
                handleResponse(frame);
                break;

            case "progress":
                handleProgress(frame);
                break;

            case "done":
            case "stopped":
                handleGenerationEnd(frame);
                break;

            case "error": {
                // Whatever was being fetched will not arrive now.
                if (!frame.channel_id || frame.channel_id === historyRequestedFor) {
                    historyRequestedFor = null;
                    set({ isHistoryLoading: false });
                }
                pendingListChannels = 0;
                set({
                    lastSocketError: { kind: "server", message: frame.message, channel_id: frame.channel_id },
                    isLoadingChannels: false,
                });
                if (get().isStreaming) failActiveStream(frame.message);
                break;
            }

            case "channel_created": {
                // Broadcast to every tab (§9): each lists the new channel, but
                // only the tab that asked for it opens it.
                set((state) =>
                    state.channels.some((c) => c.channel_id === frame.channel_id)
                        ? state
                        : {
                            channels: [
                                {
                                    channel_id: frame.channel_id,
                                    title: NEW_CHANNEL_TITLE,
                                    created_at: frame.created_at,
                                    last_updated: frame.created_at,
                                    total_messages: 0,
                                },
                                ...state.channels,
                            ],
                        },
                );

                if (!awaitingChannelCreate) break;
                awaitingChannelCreate = false;
                set({ activeChannelId: frame.channel_id });

                if (pendingPrompt) {
                    const prompt = pendingPrompt;
                    pendingPrompt = null;
                    lastAttempt = { channelId: frame.channel_id, prompt };
                    if (!dispatch({ action: "sendMessage", channel_id: frame.channel_id, prompt })) {
                        failActiveStream("Could not reach the assistant. Try again.");
                        break;
                    }
                    // CA-4: the server titles the channel from this first
                    // message and broadcasts `channel_renamed`. The client no
                    // longer sends a rename of its own — two renames on the
                    // same trigger would race.
                }
                break;
            }

            /* §5 — the assistant asks for a leads CSV. The picker opens on
               receipt; the upload goes straight to S3 and never through this
               socket, so nothing here waits on it. */
            case "lead_upload":
                handleLeadUpload(frame);
                break;

            case "template_generated":
                handleTemplateGenerated(frame);
                break;

            case "template_save_request":
                handleTemplateSaveRequest(frame);
                break;

            case "channel_list":
                handleChannelList(frame);
                break;

            case "history":
                handleHistory(frame);
                break;

            // §13 step 7 — rename and delete are applied from these broadcasts,
            // never optimistically, so every tab converges on the same list.
            case "channel_renamed": {
                // A channel this tab has not listed yet — it connected after
                // another one created it, or the list is still in flight. The
                // title has nowhere to land, so §9's sync rule applies: pull
                // the list rather than drop the rename.
                if (!get().channels.some((c) => c.channel_id === frame.channel_id)) {
                    requestChannelList();
                    break;
                }
                set((state) => ({
                    channels: state.channels.map((c) =>
                        c.channel_id === frame.channel_id ? { ...c, title: frame.title } : c,
                    ),
                }));
                break;
            }

            case "channel_deleted":
                set((state) => ({
                    channels: state.channels.filter((c) => c.channel_id !== frame.channel_id),
                }));
                // Deleted from under the user, here or on another device.
                if (frame.channel_id === get().activeChannelId) resetConversation();
                break;

            default:
                break;
        }
    };

    // ── §4 outbound ────────────────────────────────────────────────────────

    const dispatch = (action: Parameters<ChatAssistantSocket["send"]>[0]): boolean => {
        if (!socket?.send(action)) {
            set({
                lastSocketError: {
                    kind: "network",
                    message: "Not connected to the assistant. Reconnecting…",
                },
            });
            return false;
        }
        return true;
    };

    /** Guide §4 — `listChannels`. One refresh in flight is enough. */
    function requestChannelList() {
        if (pendingListChannels > 0) return;
        pendingListChannels += 1;
        set({ isLoadingChannels: true });
        if (!dispatch({ action: "listChannels" })) {
            pendingListChannels = 0;
            set({ isLoadingChannels: false });
        }
    }

    /**
     * Guide §4 — `loadHistory`. A send that fails while the socket is down is
     * not lost: `onOpen` reloads the open conversation once it is back.
     */
    function requestHistory(channelId: string) {
        historyRequestedFor = channelId;
        set({ isHistoryLoading: true });
        if (!dispatch({ action: "loadHistory", channel_id: channelId })) {
            historyRequestedFor = null;
            set({ isHistoryLoading: false });
        }
    }

    function renderOptimisticExchange(prompt: string) {
        const assistantLocalId = nextLocalId("assistant");
        const userLocalId = nextLocalId("user");
        const now = Date.now();

        set((state) => ({
            messages: [
                ...state.messages,
                {
                    clientId: userLocalId,
                    message_id: userLocalId,
                    sender: "user" as const,
                    message: prompt,
                    created_at_ms: now,
                    status: "done" as const,
                    isLocalId: true,
                },
                {
                    clientId: assistantLocalId,
                    message_id: assistantLocalId,
                    sender: "assistant" as const,
                    message: "",
                    created_at_ms: now,
                    status: "pending" as const,
                    isLocalId: true,
                },
            ],
            // No generation id exists until the first chunk arrives (§7).
            activeGenerationId: null,
            activeAssistantMessageId: assistantLocalId,
            isStreaming: true,
            isStopping: false,
            lastSocketError: null,
            lastChunkAtMs: now,
        }));
    }

    function sendToChannel(channelId: string, prompt: string) {
        lastAttempt = { channelId, prompt };
        renderOptimisticExchange(prompt);

        if (!dispatch({ action: "sendMessage", channel_id: channelId, prompt })) {
            failActiveStream("Could not reach the assistant. Try again.");
        }
    }

    /** Guide §4 — `createChannel`, then release the held prompt on the broadcast. */
    function openChannelFor(prompt: string) {
        pendingPrompt = prompt;
        awaitingChannelCreate = true;
        renderOptimisticExchange(prompt);

        if (!dispatch({ action: "createChannel" })) {
            awaitingChannelCreate = false;
            pendingPrompt = null;
            failActiveStream("Could not reach the assistant. Try again.");
        }
    }

    // ── socket lifecycle ───────────────────────────────────────────────────

    /** Leave the open conversation. The conversation list is untouched. */
    const resetConversation = () => {
        awaitingChannelCreate = false;
        clearImportTracking();
        pendingPrompt = null;
        lastAttempt = null;
        historyRequestedFor = null;
        set({
            activeChannelId: null,
            messages: EMPTY_MESSAGES,
            isHistoryLoading: false,
            activeGenerationId: null,
            activeAssistantMessageId: null,
            isStreaming: false,
            isStopping: false,
            lastChunkAtMs: null,
            /* The work the conversation produced goes with it. These outlived
               the messages: after New chat the header still offered a preview
               of a template from the conversation just cleared, and a pending
               save card survived into the empty one.

               It matters more on sign-out, which runs through here too: the
               generated document is a whole email, and leaving it in the store
               handed it to whoever signed in next on this browser — the
               builder picks a pending template up on mount. */
            generatedTemplate: null,
            templateSave: null,
            leadUpload: null,
        });
    };

    /**
     * A signed-out tab must not keep a live token-authenticated socket, and the
     * next user must not inherit this one's conversation.
     */
    const watchSignOut = () => {
        if (signOutWatcherAttached) return;
        signOutWatcherAttached = true;

        Hub.listen("auth", ({ payload }) => {
            if (payload?.event !== "signedOut") return;
            socket?.disconnect();
            socket = null;
            resetConversation();
            pendingListChannels = 0;
            titleRefetched.clear();
            set({
                socketStatus: "closed",
                lastSocketError: null,
                channels: EMPTY_CHANNELS,
                isLoadingChannels: false,
                channelsError: null,
            });
        });
    };

    const ensureSocket = (): ChatAssistantSocket => {
        watchSignOut();
        if (socket) return socket;

        socket = new ChatAssistantSocket({
            onStatus: (status) => {
                set({ socketStatus: status });

                // Losing the socket mid-generation ends the local stream. The reply
                // keeps running server-side; the history reload in `onOpen`
                // brings back whatever was stored.
                if (status !== "open" && get().isStreaming) {
                    failActiveStream("Connection lost before the reply finished.");
                }
            },

            onEvent: handleEvent,

            onError: (error) => {
                /* An untyped API Gateway envelope names no action. When a list
                   refresh is outstanding and no reply is waiting to start, it
                   answers the refresh — report it there, not as a banner that
                   reads as though the user's message failed. */
                const replyStarting = get().isStreaming && get().activeGenerationId === null;
                if (pendingListChannels > 0 && error.kind === "server" && !replyStarting) {
                    pendingListChannels = 0;
                    set({ isLoadingChannels: false, channelsError: error.message });
                    return;
                }

                set({ lastSocketError: error });
                if (error.kind === "server" && historyRequestedFor) {
                    historyRequestedFor = null;
                    set({ isHistoryLoading: false });
                }

                // Abandon an in-flight generation only when it cannot continue:
                // the socket is gone, or no chunk has bound yet so the reply never
                // started. Chunks already flowing mean the generation is alive.
                const { isStreaming, socketStatus, activeGenerationId } = get();
                if (isStreaming && (socketStatus !== "open" || activeGenerationId === null)) {
                    failActiveStream(error.message);
                }
            },

            /**
             * Guide §12: after every (re)connect, refresh the list and reload the
             * open conversation, which recovers anything completed while
             * disconnected. `sendMessage` is never replayed — the server may
             * already have accepted it.
             */
            onOpen: () => {
                set({ lastSocketError: null });
                pendingListChannels = 0;
                requestChannelList();
                const { activeChannelId } = get();
                if (activeChannelId) requestHistory(activeChannelId);
            },
        });

        return socket;
    };

    // ── public API ─────────────────────────────────────────────────────────

    return {
        socketStatus: "closed",
        activeChannelId: null,
        channels: EMPTY_CHANNELS,
        messages: EMPTY_MESSAGES,
        activeGenerationId: null,
        activeAssistantMessageId: null,
        isStreaming: false,
        lastSocketError: null,
        isStopping: false,
        isLoadingChannels: false,
        channelsError: null,
        isHistoryLoading: false,
        lastChunkAtMs: null,
        leadUpload: null,
        generatedTemplate: null,
        templateSave: null,

        connect: () => {
            void ensureSocket().connect();
        },

        disconnect: () => {
            socket?.disconnect();
        },

        retryConnection: () => {
            const instance = ensureSocket();
            instance.resetRetryState();
            set({ lastSocketError: null });
            void instance.connect();
        },

        sendPrompt: (prompt) => {
            const text = prompt.trim();
            const { isStreaming, activeChannelId } = get();
            if (!text || isStreaming) return;

            if (activeChannelId) sendToChannel(activeChannelId, text);
            else openChannelFor(text);
        },

        // Guide §8 — cancellation is best effort; `stopped` does the finalizing.
        stopMessage: () => {
            const { activeChannelId, isStreaming } = get();
            if (!isStreaming || !activeChannelId) return;
            set({ isStopping: true });
            dispatch({ action: "stopMessage", channel_id: activeChannelId });
        },

        listChannels: () => requestChannelList(),

        renameChannel: (channelId, title) => {
            const trimmed = title.trim();
            if (!channelId || !trimmed) return;
            dispatch({ action: "renameChannel", channel_id: channelId, title: trimmed });
        },

        deleteChannel: (channelId) => {
            if (!channelId) return;
            dispatch({ action: "deleteChannel", channel_id: channelId });
        },

        /**
         * Always fetched on open, never served from memory: the conversation may
         * have moved on from another tab or device since this one last saw it.
         *
         * Switching away from a reply that is still streaming leaves it running on
         * the server; reopening the conversation loads what it has stored, and its
         * `done` reloads it again (§9).
         */
        selectChannel: (channelId) => {
            if (!channelId || channelId === get().activeChannelId) return;
            resetConversation();
            set({ activeChannelId: channelId, lastSocketError: null });
            requestHistory(channelId);
        },

        clearGeneratedTemplate: () => set({ generatedTemplate: null }),

        submitTemplateName: async (name) => {
            const trimmed = name.trim();
            if (!trimmed) return;
            await handleTemplateSaveRequest({ type: "template_save_request", name: trimmed });
        },

        dismissTemplateSave: () => set({ templateSave: null }),

        startNewChat: () => {
            // The next prompt opens a fresh channel; deferring `createChannel`
            // until then keeps empty conversations out of the list. The previous
            // one stays in the list, to be reopened from there.
            resetConversation();
            set({ lastSocketError: null });
        },

        retryLastPrompt: () => {
            const attempt = lastAttempt;
            if (!attempt || get().isStreaming) return;
            set({ lastSocketError: null });
            sendToChannel(attempt.channelId, attempt.prompt);
        },

        canRetry: () => lastAttempt !== null && !get().isStreaming,

        clearError: () => set({ lastSocketError: null }),

        /**
         * CA-3 retry. Asks the assistant for a fresh upload target rather than
         * reusing the old one: the presigned POST is single-use and expires, so
         * replaying it can only fail with an opaque 403.
         */
        canRetryContactImport: () => lastImportGroupId !== null && !get().isStreaming,

        retryContactImport: () => {
            const groupId = lastImportGroupId;
            const { isStreaming, activeChannelId } = get();
            if (groupId === null || isStreaming) return false;

            const prompt = `Import contacts into audience ${groupId} again — the last CSV import failed. Please send a new upload link.`;
            set({ lastSocketError: null, leadUpload: null });
            if (activeChannelId) sendToChannel(activeChannelId, prompt);
            else openChannelFor(prompt);
            return true;
        },
    };
});

/** Test seam — drops the socket and all cross-render internals. */
export function __resetChatAssistant() {
    socket?.disconnect();
    socket = null;
    awaitingChannelCreate = false;
    pendingPrompt = null;
    lastAttempt = null;
    pendingListChannels = 0;
    historyRequestedFor = null;
    titleRefetched.clear();
    clearImportTracking();
}
